import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ServiceTimeline from './components/ServiceTimeline';
import ActionButtons from './components/ActionButtons';
import RescheduleModal from './components/RescheduleModal';
import QuoteDetailsPanel from './components/QuoteDetailsPanel';
import InterventionDetailsPanel from './components/InterventionDetailsPanel';
import {
  canShowCancelAction,
  canRescheduleAppointment,
  getAppointmentTabLabel,
  isQuoteAppointment,
  isQuoteListItem,
  sortAppointmentsByDisplayDate,
  sortAppointmentsByScheduledDate,
} from './appointmentTiming';
import { trackingService } from '../../services/trackingService';
import { supabase } from '../../lib/supabase';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import { buildQuoteAcceptBookingDraft } from '../../utils/quoteAcceptBooking';
import { useBrandSettings } from '../../utils/useBrandSettings';

/**
 * @param {'interventions' | 'devis'} view
 */
const PublicServiceTracking = ({ view = 'interventions' }) => {
  const navigate = useNavigate();
  const isQuotesView = view === 'devis';
  const { brandName } = useBrandSettings();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Real-time state from Supabase
  const [appointments, setAppointments] = useState([]);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [technician, setTechnician] = useState(null);
  const [technicianLocation, setTechnicianLocation] = useState(null);
  const [customerLocation, setCustomerLocation] = useState(null);
  const [currentStatus, setCurrentStatus] = useState('');
  const [statusHistory, setStatusHistory] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [eta, setEta] = useState(null);
  const [userId, setUserId] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [appointmentQuote, setAppointmentQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState(null);
  // Récupérer l'utilisateur connecté
  useEffect(() => {
    const getUser = async () => {
      try {
        console.log('Récupération de l\'utilisateur...');
        const { data: { user }, error: userError } = await supabase.auth.getUser();
        
        if (userError) {
          console.error('Erreur auth:', userError);
          setError(`Erreur d'authentification: ${userError.message}`);
          return;
        }
        
        if (user) {
          console.log('Utilisateur connecté:', user.id);
          setUserId(user.id);
          setCurrentUser({
            id: user.id,
            name: user.user_metadata?.full_name || user.email?.split('@')[0] || 'Utilisateur',
            email: user.email,
            role: user.user_metadata?.role || 'user',
          });
        } else {
          console.log('Aucun utilisateur connecté, redirection...');
          navigate('/login');
        }
      } catch (err) {
        console.error('Exception auth:', err);
        setError(`Erreur d'authentification: ${err.message}`);
      }
    };
    getUser();

  }, [navigate]);

  // Charger tous les rendez-vous du client - VERSION CORRIGÉE
  useEffect(() => {
    if (!userId) return;

    const loadClientAppointments = async () => {
      try {
        setIsLoading(true);
        console.log('Chargement des rendez-vous pour le client:', userId);
        
        // 1. Récupérer d'abord les rendez-vous sans la jointure
        const { data: appointmentsData, error: appointmentsError } = await supabase
          .from('appointments')
          .select(`
            *,
            Service:Service(
              id,
              name,
              PricingModel,
              basePrice,
              estimatedDuration,
              TVA,
              problem:problem(
                id,
                name,
                equipment:equipment(
                  id,
                  name,
                  category:category(id, name)
                )
              )
            ),
            appointment_quote(id, status)
          `)
          .eq('client', userId)
          .order('created_at', { ascending: false });

        if (appointmentsError) {
          console.error('Erreur récupération appointments:', appointmentsError);
          throw appointmentsError;
        }

        console.log('Rendez-vous trouvés:', appointmentsData?.length || 0);

        if (!appointmentsData || appointmentsData.length === 0) {
          setAppointments([]);
          setSelectedAppointment(null);
          setIsLoading(false);
          return;
        }

        // 2. Pour chaque rendez-vous, récupérer les infos du technicien séparément
        const transformedAppointments = await Promise.all(
          appointmentsData.map(async (apt) => {
            let technicianData = null;

            const quotes = Array.isArray(apt.appointment_quote)
              ? apt.appointment_quote
              : apt.appointment_quote
                ? [apt.appointment_quote]
                : [];
            const latestQuote = quotes[0] || null;
            
            if (apt.technician) {
              // Récupérer les infos du technicien
              const { data: techData, error: techError } = await supabase
                .from('user_profiles')
                .select('id, full_name, email, phone, avatar_url, specialty, rating, review_count, current_latitude, current_longitude')
                .eq('id', apt.technician)
                .single();

              if (!techError && techData) {
                technicianData = techData;
              }
            }

            return {
              id: apt.id,
              reference: apt.reference,
              status: apt.status,
              isQuote: Boolean(apt.is_quote),
              hasQuote: Boolean(latestQuote?.id || apt.is_quote),
              quoteStatus: latestQuote?.status || null,
              createdAt: apt.created_at,
              scheduledDate: apt.scheduled_date,
              scheduledTimeStart: apt.scheduled_time_start,
              scheduledTimeEnd: apt.scheduled_time_end,
              serviceType: apt.Service?.name || apt.service_type,
              problemName: apt.Service?.problem?.name || null,
              service: apt.Service || null,
              urgency: apt.urgency,
              customerName: apt.customer_name,
              customerEmail: apt.customer_email,
              customerPhone: apt.customer_phone,
              customerAddress: apt.customer_address,
              customerLatitude: apt.customer_latitude,
              customerLongitude: apt.customer_longitude,
              notes: apt.notes,
              priceSubtotal: apt.price_subtotal,
              priceVat: apt.price_vat,
              priceTotal: apt.price_total,
              paymentMethod: apt.payment_method,
              estimatedArrivalTime: apt.estimated_arrival_time,
              actualArrivalTime: apt.actual_arrival_time,
              completionTime: apt.completion_time,
              technician: technicianData ? {
                id: technicianData.id,
                name: technicianData.full_name,
                email: technicianData.email,
                phone: technicianData.phone,
                avatarUrl: technicianData.avatar_url,
                specialty: technicianData.specialty,
                rating: technicianData.rating,
                reviewCount: technicianData.review_count,
                currentLatitude: technicianData.current_latitude,
                currentLongitude: technicianData.current_longitude
              } : null
            };
          })
        );

        setAppointments(transformedAppointments);

        const interventions = sortAppointmentsByScheduledDate(
          transformedAppointments.filter((apt) => !apt.isQuote)
        );
        const quotes = sortAppointmentsByDisplayDate(
          transformedAppointments.filter((apt) => isQuoteListItem(apt))
        );
        const visibleList = isQuotesView ? quotes : interventions;
        const latestAppointment = visibleList[0] || null;

        setSelectedAppointment(latestAppointment);

        if (latestAppointment) {
          await loadAppointmentDetails(latestAppointment.id, latestAppointment, {
            forceQuoteView: isQuotesView,
          });
        }
        
      } catch (err) {
        console.error('Erreur dans loadClientAppointments:', err);
        setError(err?.message || 'Erreur lors du chargement des rendez-vous');
      } finally {
        setIsLoading(false);
      }
    };

    loadClientAppointments();
  }, [userId, isQuotesView]);

  const loadAppointmentQuote = async (appointmentId, isQuote) => {
    if (!appointmentId || !isQuote) {
      setAppointmentQuote(null);
      setQuoteError(null);
      setQuoteLoading(false);
      return;
    }

    setQuoteLoading(true);
    setQuoteError(null);

    try {
      const { data, error } = await supabase
        .from('appointment_quote')
        .select('*')
        .eq('appointment_id', appointmentId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      setAppointmentQuote(data || null);
    } catch (err) {
      console.error('Erreur chargement devis:', err);
      setAppointmentQuote(null);
      setQuoteError(err?.message || 'Erreur lors du chargement du devis');
    } finally {
      setQuoteLoading(false);
    }
  };

  // Charger les détails d'un rendez-vous spécifique
  const loadAppointmentDetails = async (appointmentId, appointmentOverride = null, options = {}) => {
    try {
      console.log('Chargement des détails pour le rendez-vous:', appointmentId);

      const apt =
        appointmentOverride ||
        appointments.find((a) => a.id === appointmentId) ||
        (selectedAppointment?.id === appointmentId ? selectedAppointment : null);

      const treatAsQuote =
        options.forceQuoteView ||
        isQuoteAppointment(apt) ||
        Boolean(apt?.hasQuote);

      if (treatAsQuote) {
        await loadAppointmentQuote(appointmentId, true);
        setCurrentStatus(apt?.status || '');
        setTechnician(null);
        setTechnicianLocation(null);
        setCustomerLocation(null);
        setStatusHistory([]);
        setNotifications([]);
        setEta(null);
        return;
      }

      setAppointmentQuote(null);
      setQuoteError(null);
      
      // Récupérer l'historique des statuts
      const { data: statusUpdatesData, error: historyError } = await supabase
        .from('status_updates')
        .select('new_status, created_at, notes')
        .eq('appointment_id', appointmentId)
        .order('created_at', { ascending: true });

      if (historyError) {
        console.error('Erreur historique:', historyError);
      }

      // Récupérer les notifications
      const { data: notificationsData, error: notificationsError } = await supabase
        .from('notifications')
        .select('*')
        .eq('appointment_id', appointmentId)
        .order('created_at', { ascending: false });

      if (notificationsError) {
        console.error('Erreur notifications:', notificationsError);
      }

      // Mettre à jour les états
      setStatusHistory(statusUpdatesData?.map((entry) => ({
        id: entry.created_at,
        status: entry.new_status,
        timestamp: entry.created_at,
        note: entry.notes,
      })) || []);

      setNotifications(notificationsData?.map(notif => ({
        id: notif.id,
        type: notif.type,
        title: notif.title,
        message: notif.message,
        timestamp: new Date(notif.created_at),
        actionable: notif.actionable
      })) || []);

      // Trouver le rendez-vous sélectionné
      if (apt) {
        setCurrentStatus(apt.status);
        setTechnician(apt.technician);
        
        if (apt.technician?.currentLatitude && apt.technician?.currentLongitude) {
          setTechnicianLocation({
            lat: apt.technician.currentLatitude,
            lng: apt.technician.currentLongitude
          });
        }
        
        if (apt.customerLatitude && apt.customerLongitude) {
          setCustomerLocation({
            lat: apt.customerLatitude,
            lng: apt.customerLongitude
          });
        }

        // Calculer l'ETA
        if (apt.id && apt.technician) {
          try {
            const calculatedEta = await trackingService?.calculateETA(apt.id);
            setEta(calculatedEta);
          } catch (etaErr) {
            console.error('Erreur calcul ETA:', etaErr);
          }
        }
      }
    } catch (err) {
      console.error('Erreur dans loadAppointmentDetails:', err);
    }
  };

  // Changer de rendez-vous
  const handleQuoteDecision = async (nextStatus, refusalPayload = null) => {
    if (!appointmentQuote?.id) {
      throw new Error('Devis introuvable');
    }

    const updateData = {
      status: nextStatus,
      updated_at: new Date().toISOString(),
    };

    if (nextStatus === 'rejected' && refusalPayload) {
      updateData.refusal_reason = refusalPayload.refusal_reason || null;
      updateData.refusal_comment = refusalPayload.refusal_comment || null;
      updateData.refused_at = refusalPayload.refused_at || new Date().toISOString();
    }

    const { data, error } = await supabase
      .from('appointment_quote')
      .update(updateData)
      .eq('id', appointmentQuote.id)
      .select('*')
      .single();

    if (error) throw error;
    setAppointmentQuote(data);
  };

  const handleStartAcceptBooking = () => {
    if (!selectedAppointment || !appointmentQuote) return;

    const bookingDraft = buildQuoteAcceptBookingDraft(selectedAppointment, appointmentQuote);
    if (!bookingDraft) return;

    navigate('/quote-accept-booking', {
      state: { bookingData: bookingDraft },
    });
  };

  const handleAppointmentChange = (appointmentId) => {
    const apt = appointments.find(a => a.id === appointmentId);
    if (apt) {
      setSelectedAppointment(apt);
      loadAppointmentDetails(appointmentId, apt, { forceQuoteView: isQuotesView });
    }
  };

  const visibleAppointments = useMemo(
    () =>
      isQuotesView
        ? sortAppointmentsByDisplayDate(
            appointments.filter((apt) => isQuoteListItem(apt))
          )
        : sortAppointmentsByScheduledDate(
            appointments.filter((apt) => !apt.isQuote)
          ),
    [appointments, isQuotesView]
  );

  const handleReschedule = () => {
    if (!canRescheduleAppointment({
      urgency: selectedAppointment?.urgency,
      scheduledDate: selectedAppointment?.scheduledDate,
      scheduledTimeStart: selectedAppointment?.scheduledTimeStart,
      status: currentStatus || selectedAppointment?.status,
    })) {
      return;
    }
    setShowRescheduleModal(true);
  };

  const handleRescheduleSuccess = (updatedSchedule) => {
    const updated = {
      ...selectedAppointment,
      scheduledDate: updatedSchedule.scheduledDate,
      scheduledTimeStart: updatedSchedule.scheduledTimeStart,
      scheduledTimeEnd: updatedSchedule.scheduledTimeEnd,
      urgency: 'tomorrow',
    };

    setSelectedAppointment(updated);
    setAppointments((prev) =>
      prev.map((apt) => (apt.id === updated.id ? updated : apt))
    );
    loadAppointmentDetails(updated.id);
  };

  const handleCancelService = () => {
    console.log('Annuler l\'intervention');
    console.log(selectedAppointment?.scheduledTimeStart);
   // navigate('/');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-muted-foreground">Chargement de vos rendez-vous...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="max-w-md p-6 bg-card rounded-lg border border-border">
          <div className="flex items-center gap-3 mb-4">
            <Icon name="AlertCircle" size={32} className="text-error" />
            <h2 className="text-xl font-semibold text-foreground">Erreur de chargement</h2>
          </div>
          <p className="text-muted-foreground mb-4">{error}</p>
          <Button onClick={() => window.location?.reload()} variant="outline">
            Réessayer
          </Button>
        </div>
      </div>
    );
  }

  const canReschedule = selectedAppointment && !isQuoteAppointment(selectedAppointment)
    ? canRescheduleAppointment({
        urgency: selectedAppointment.urgency,
        scheduledDate: selectedAppointment.scheduledDate,
        scheduledTimeStart: selectedAppointment.scheduledTimeStart,
        status: currentStatus || selectedAppointment.status,
      })
    : false;

  const renderEmptyState = () => (
    <div className="max-w-md mx-auto p-6 bg-card rounded-lg border border-border text-center">
      <Icon name="Calendar" size={48} className="text-muted-foreground mx-auto mb-4" />
      {isQuotesView ? (
        <>
          <h2 className="text-xl font-semibold text-foreground mb-2">Aucun devis</h2>
          <p className="text-muted-foreground">
            Vous n&apos;avez aucune demande de devis en cours.
          </p>
        </>
      ) : (
        <>
          <h2 className="text-xl font-semibold text-foreground mb-2">
            Aucune demande d&apos;intervention réalisée pour le moment
          </h2>
          <p className="text-muted-foreground mb-4">
            Réservez une intervention pour la suivre ici.
          </p>
          <Button onClick={() => navigate('/service-booking-wizard')} variant="default">
            Réserver une intervention
          </Button>
        </>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedHeader
        userRole={currentUser?.role}
        userName={currentUser?.name || 'Client'}
      />

      <main className="main-content max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-6 sm:pb-8">
        <section className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-foreground mb-4 break-normal">
            {isQuotesView ? 'Mes devis' : "Mes interventions"}
          </h1>

          {visibleAppointments.length > 0 && (
            <div
              className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-thin"
              role="tablist"
              aria-label={
                isQuotesView ? 'Liste des devis' : "Liste des demandes d'intervention"
              }
            >
              {visibleAppointments.map((apt) => {
                const isSelected = selectedAppointment?.id === apt.id;
                return (
                  <button
                    key={apt.id}
                    type="button"
                    onClick={() => handleAppointmentChange(apt.id)}
                    className={`px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm whitespace-nowrap transition-colors border ${
                      isSelected
                        ? 'bg-success text-success-foreground border-success'
                        : 'bg-muted/60 text-muted-foreground border-border hover:bg-muted'
                    }`}
                  >
                    {getAppointmentTabLabel(apt, isQuotesView ? 'devis' : 'interventions')}
                  </button>
                );
              })}
            </div>
          )}
        </section>

        {visibleAppointments.length === 0 ? (
          renderEmptyState()
        ) : isQuotesView ? (
          <QuoteDetailsPanel
            appointment={selectedAppointment}
            quote={appointmentQuote}
            quoteLoading={quoteLoading}
            quoteError={quoteError}
            onRetryQuote={() => loadAppointmentQuote(selectedAppointment?.id, true)}
            onQuoteDecision={handleQuoteDecision}
            onAcceptBooking={handleStartAcceptBooking}
            onContinueBooking={handleStartAcceptBooking}
          />
        ) : (
          <div className="space-y-6">
            <InterventionDetailsPanel
              appointment={selectedAppointment}
              status={currentStatus || selectedAppointment?.status}
            />

            <ServiceTimeline
              currentStatus={currentStatus || selectedAppointment?.status}
              statusHistory={statusHistory}
              scheduledDate={selectedAppointment?.scheduledDate}
              scheduledTimeStart={selectedAppointment?.scheduledTimeStart}
              actualArrivalTime={selectedAppointment?.actualArrivalTime}
              completionTime={selectedAppointment?.completionTime}
            />

            <ActionButtons
              onReschedule={handleReschedule}
              onCancelService={handleCancelService}
              canReschedule={canReschedule}
              canCancel={canShowCancelAction({
                status: currentStatus || selectedAppointment?.status,
                scheduledDate: selectedAppointment?.scheduledDate,
                scheduledTimeStart: selectedAppointment?.scheduledTimeStart,
              })}
              scheduledDate={selectedAppointment?.scheduledDate}
              scheduledTimeStart={selectedAppointment?.scheduledTimeStart}
            />

            {selectedAppointment && (
              <RescheduleModal
                open={showRescheduleModal}
                appointment={selectedAppointment}
                onClose={() => setShowRescheduleModal(false)}
                onSuccess={handleRescheduleSuccess}
              />
            )}
          </div>
        )}

        <div className="mt-8 p-4 sm:p-6 bg-card rounded-lg border border-border">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Icon name="Shield" size={24} className="text-primary" />
            </div>
            <div className="flex-1">
              <h3 className="text-base font-semibold text-foreground mb-2">
                Vos données sont protégées
              </h3>
              <p className="text-sm text-muted-foreground mb-3">
                Vos données et votre localisation ne sont partagées qu'avec votre accord. Nous respectons strictement le RGPD et vos informations sont sécurisées.
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-muted rounded-full text-xs font-medium text-foreground">
                  Conforme RGPD
                </span>
                <span className="px-3 py-1 bg-muted rounded-full text-xs font-medium text-foreground">
                  Données chiffrées
                </span>
                <span className="px-3 py-1 bg-muted rounded-full text-xs font-medium text-foreground">
                  Paiement sécurisé
                </span>
              </div>
            </div>
          </div>
        </div>
      </main>

      <footer className="bg-card border-t border-border mt-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Contact + Liens rapides masqués pour le moment */}
          <div className="text-center">
            <p className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} {brandName}. Tous droits réservés.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PublicServiceTracking;
